import { useParams, Link, Navigate } from "react-router-dom";
import { motion } from "framer-motion";
import { ArrowRight, ArrowLeft, CheckCircle2, Sparkles, GraduationCap, Cloud, CheckSquare, Code2 } from "lucide-react";
import { PRODUCTS, IMAGES } from "@/lib/data";
import BgLayer from "@/components/ui-shared/BgLayer";

const iconMap = {
    "enterprise-lxp": Sparkles,
    "elearning": GraduationCap,
    "cloud-labs": Cloud,
    "ai-assessments": CheckSquare,
    "ai-coding": Code2,
};

const ProductDetailPage = () => {
    const { slug } = useParams();
    const product = PRODUCTS.find((p) => p.slug === slug);
    if (!product) return <Navigate to="/products" replace />;
    const Icon = iconMap[slug] || Sparkles;
    const otherProducts = PRODUCTS.filter((p) => p.slug !== slug).slice(0, 4);

    return (
        <div data-testid={`product-page-${slug}`}>
            {/* Hero */}
            <section className="relative pt-32 md:pt-40 pb-16 overflow-hidden">
                <BgLayer variant="mesh" intensity="strong" />
                <div className="sxe-container relative grid lg:grid-cols-[1.2fr_1fr] gap-12 items-center">
                    <div>
                        <Link to="/products" className="inline-flex items-center gap-1.5 text-[#B0A3C4] hover:text-white text-sm font-display">
                            <ArrowLeft className="w-3.5 h-3.5" /> All Products
                        </Link>
                        <div className="mt-4 overline"><span className="dot" /> Product · {product.tagline}</div>
                        <h1 className="font-display text-5xl md:text-6xl lg:text-7xl font-medium tracking-tighter leading-[1.02] text-white mt-5">
                            {product.title.split(" ").slice(0, -1).join(" ")}{" "}
                            <span className="text-gradient">{product.title.split(" ").slice(-1)}</span>
                        </h1>
                        <p className="text-[#B0A3C4] text-lg md:text-xl leading-relaxed mt-6 max-w-xl">
                            {product.longIntro}
                        </p>
                        <div className="mt-8 flex flex-wrap gap-3">
                            <a href="#contact" className="btn-primary" data-testid={`product-page-cta-experts-${slug}`}>
                                Talk to Our Experts <ArrowRight className="w-4 h-4" />
                            </a>
                            <Link to="/products" className="btn-secondary">See All Products</Link>
                        </div>
                        <div className="mt-8 flex flex-wrap gap-2">
                            {product.highlights.map((h) => (
                                <span key={h} className="sxe-chip">{h}</span>
                            ))}
                        </div>
                    </div>
                    <motion.div
                        initial={{ opacity: 0, scale: 0.97 }}
                        animate={{ opacity: 1, scale: 1 }}
                        transition={{ duration: 0.8 }}
                        className="relative rounded-2xl overflow-hidden border border-[#C266FF]/20 sxe-card p-0"
                    >
                        <img src={product.image} alt={product.title} className="w-full h-[360px] object-cover" />
                        <div className="absolute inset-0 bg-gradient-to-t from-[#07020F] via-[#07020F]/20 to-transparent" />
                        <div className="absolute bottom-5 left-5 right-5 flex items-center gap-3">
                            <div className="w-12 h-12 rounded-xl bg-gradient-to-br from-[#A100FF] to-[#7B00CC] flex items-center justify-center">
                                <Icon className="w-6 h-6 text-white" />
                            </div>
                            <div>
                                <div className="text-[11px] uppercase tracking-widest text-[#E9CCFF] font-display">SpringXE Platform</div>
                                <div className="font-display text-white text-lg font-medium">{product.title}</div>
                            </div>
                        </div>
                    </motion.div>
                </div>
            </section>

            {/* Capabilities */}
            <section className="sxe-section relative bg-[#08031a]">
                <BgLayer variant="dots" />
                <div className="sxe-container relative">
                    <div className="max-w-3xl">
                        <div className="overline"><span className="dot" /> Capabilities</div>
                        <h2 className="font-display text-4xl md:text-5xl font-medium tracking-tighter leading-[1.05] text-white mt-4">
                            Every layer engineered for <span className="text-gradient">enterprise scale</span>.
                        </h2>
                    </div>
                    <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-5 mt-12">
                        {product.capabilities.map((c, i) => (
                            <motion.div
                                key={c.title}
                                initial={{ opacity: 0, y: 20 }}
                                whileInView={{ opacity: 1, y: 0 }}
                                viewport={{ once: true, margin: "-40px" }}
                                transition={{ duration: 0.5, delay: i * 0.05 }}
                                className="sxe-card p-6"
                                data-testid={`product-capability-${slug}-${i}`}
                            >
                                <div className="w-10 h-10 rounded-lg bg-gradient-to-br from-[#A100FF]/25 to-[#7B00CC]/5 border border-[#C266FF]/25 flex items-center justify-center">
                                    <span className="font-mono text-[11px] text-[#E9CCFF]">0{i + 1}</span>
                                </div>
                                <h3 className="font-display text-white text-lg font-medium tracking-tight mt-4">{c.title}</h3>
                                <p className="text-[#B0A3C4] text-[13.5px] leading-relaxed mt-2">{c.desc}</p>
                            </motion.div>
                        ))}
                    </div>
                </div>
            </section>

            {/* Outcomes + visual */}
            <section className="sxe-section relative">
                <BgLayer variant="orbs" />
                <div className="sxe-container relative grid lg:grid-cols-2 gap-12 items-center">
                    <div>
                        <div className="overline"><span className="dot" /> Business Outcomes</div>
                        <h2 className="font-display text-4xl md:text-5xl font-medium tracking-tighter leading-[1.05] text-white mt-4">
                            Outcomes leadership can <span className="text-gradient">measure</span>.
                        </h2>
                        <ul className="mt-8 space-y-3 max-w-xl">
                            {product.outcomes.map((o, i) => (
                                <li key={i} className="flex items-start gap-3 text-[#E9CCFF]">
                                    <CheckCircle2 className="w-5 h-5 text-[#C266FF] shrink-0 mt-0.5" />
                                    <span className="text-[15px] leading-relaxed">{o}</span>
                                </li>
                            ))}
                        </ul>
                    </div>
                    <motion.div
                        initial={{ opacity: 0, y: 20 }}
                        whileInView={{ opacity: 1, y: 0 }}
                        viewport={{ once: true }}
                        transition={{ duration: 0.6 }}
                        className="relative rounded-2xl overflow-hidden border border-[#C266FF]/20"
                    >
                        <img src={IMAGES.enterpriseTeam} alt="Enterprise team" className="w-full h-[380px] object-cover" />
                        <div className="absolute inset-0 bg-gradient-to-t from-[#07020F] via-transparent to-transparent" />
                    </motion.div>
                </div>
            </section>

            {/* Other products */}
            <section className="sxe-section relative bg-[#08031a]">
                <BgLayer variant="dots" />
                <div className="sxe-container relative">
                    <div className="flex items-end justify-between flex-wrap gap-4">
                        <div>
                            <div className="overline"><span className="dot" /> Continue exploring</div>
                            <h2 className="font-display text-3xl md:text-4xl font-medium tracking-tight text-white mt-3">Other SpringXE products</h2>
                        </div>
                        <Link to="/products" className="btn-secondary">All Products <ArrowRight className="w-4 h-4" /></Link>
                    </div>
                    <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-5 mt-10">
                        {otherProducts.map((p) => {
                            const OI = iconMap[p.slug] || Sparkles;
                            return (
                                <Link key={p.slug} to={`/products/${p.slug}`} className="sxe-card p-6 group">
                                    <OI className="w-6 h-6 text-[#C266FF]" />
                                    <div className="font-display text-white text-lg font-medium tracking-tight mt-4">{p.title}</div>
                                    <div className="text-[#B0A3C4] text-[13px] leading-relaxed mt-1.5">{p.tagline}</div>
                                    <div className="mt-4 inline-flex items-center gap-1 text-[#E9CCFF] text-sm font-display opacity-0 group-hover:opacity-100 transition-opacity">
                                        Learn more <ArrowRight className="w-3.5 h-3.5" />
                                    </div>
                                </Link>
                            );
                        })}
                    </div>
                </div>
            </section>
        </div>
    );
};

export default ProductDetailPage;
